// @ts-check
import Vue from 'vue'
import API from '@/store/apiUtils'
import FIELD from '@/helpers/fieldHelper'
import allScholasticYears from '@/store/allScholasticYears'
import {
  STREAM,
  STATUS,
  APPLICATION_OFFER_TYPE_NAME,
  EA_TAB_FILTERING
} from '@/constants'
import { searchFilter } from '@/helpers/searchHelper'
import _isEmpty from 'lodash/isEmpty'
import moment from 'moment'
import router from '@/router'
import { getEnrolmentType } from '@/helpers/applicationDataHelper'

/**
 * @typedef {import('../../types/supportingDocuments').DocumentStatus} DocumentStatus
 * @typedef {import('../../types/supportingDocuments').DocumentDetail} DocumentDetail
 * @typedef {import('../../types/supportingDocuments').DocStatusesByCategory} DocStatusesByCategory
 * @typedef {import('../../types/supportingDocuments').DocDetailsByCategory} DocDetailsByCategory
 */

/**
 * Merges document details with their corresponding status information by category
 * @param {DocDetailsByCategory} docDetails - Object containing arrays of document details grouped by category
 * @param {DocStatusesByCategory} docStatuses - Object containing arrays of document statuses grouped by category
 * @returns {Object.<string, Array<DocumentStatus & DocumentDetail>>} Merged document information grouped by category
 */

export const mergeDocuments = (docDetails, docStatuses) => {
  const result = {}
  // Get all categories
  const categories = [
    ...new Set([...Object.keys(docDetails), ...Object.keys(docStatuses)])
  ]

  // Merge documents for each category
  categories.forEach((category) => {
    const categoryDetails = docDetails[category] || []
    const categoryStatuses = docStatuses[category] || []

    result[category] = categoryStatuses.map((status) => {
      const docInfo =
        categoryDetails.find((doc) => status.objectKey === doc.objectKey) || {}

      return {
        ...status,
        ...docInfo
      }
    })
  })

  return result
}

const checkStartDateInsideRange = (startDateFilter, intendedStartDate) => {
  const start = moment(startDateFilter[0])
  const end = moment(startDateFilter[1])
  const startDate = moment(intendedStartDate)

  return start <= startDate && startDate <= end
}

const filterApps = (
  applications,
  search,
  statusFilter,
  scholasticYearFilter,
  startYearFilter,
  startDateFilter,
  applicationTypeFilter,
  intakeFilter,
  srnLinkFilter
) => {
  return applications.filter((a) => {
    const isName =
      (search || '') !== '' ? searchFilter(a.name.split(', '), search) : true
    const isStatus =
      statusFilter && statusFilter.length
        ? statusFilter.includes(a.applicationStatus)
        : true
    const isScholasticYear =
      scholasticYearFilter && scholasticYearFilter.length
        ? scholasticYearFilter.includes(a.scholasticYear)
        : true
    let isStartDate = true
    if (startYearFilter && startYearFilter.length) {
      isStartDate = startYearFilter.includes(
        new Date(a.intendedStartDate).getFullYear().toString()
      )
    } else if (startDateFilter && startDateFilter.length) {
      isStartDate = checkStartDateInsideRange(
        startDateFilter,
        a.intendedStartDate
      )
    }
    const itemApplicationType = getEnrolmentType(a)
    const isApplicationType =
      applicationTypeFilter && applicationTypeFilter.length
        ? applicationTypeFilter.includes(itemApplicationType)
        : true
    const isIntake =
      intakeFilter && intakeFilter.length
        ? intakeFilter.includes(a.inArea)
        : true
    const isSrnLinked =
      srnLinkFilter && srnLinkFilter.length
        ? srnLinkFilter.includes(a.srn ? 'Linked' : 'Unlinked')
        : true
    return (
      isName &&
      isStatus &&
      isScholasticYear &&
      isStartDate &&
      isIntake &&
      isSrnLinked &&
      isApplicationType
    )
  })
}

const applicationsModule = {
  state: {
    localAreaApplications: {},
    searchApplicationsFilter: '',
    statusFilter: [],
    scholasticYearFilter: [],
    startYearFilter: [],
    startDateFilter: [],
    applicationTypeFilter: [],
    intakeFilter: [],
    srnLinkFilter: [],
    defaultPage: 50
  },
  getters: {
    localAreaApplications: (state) => (schoolId) => {
      return state.localAreaApplications[schoolId] || []
    },
    searchApplicationsFilter: (state) => state.searchApplicationsFilter,

    // processed
    processedApplications: (state, getters) => (schoolId) => {
      return getters
        .localAreaApplications(schoolId)
        .filter((a) => a.isProcessed)
    },
    processedApplicationsFiltered: (state, getters) => (schoolId) => {
      const applications = getters.processedApplications(schoolId)
      return filterApps(
        applications,
        state.searchApplicationsFilter,
        state.statusFilter,
        state.scholasticYearFilter,
        state.startYearFilter,
        state.startDateFilter,
        state.applicationTypeFilter,
        state.intakeFilter,
        state.srnLinkFilter
      )
    },

    // pending
    pendingApplications: (state, getters) => (schoolId) => {
      return getters
        .localAreaApplications(schoolId)
        .filter((a) => !a.isProcessed)
    },
    pendingApplicationsFiltered: (state, getters) => (schoolId) => {
      const applications = getters.pendingApplications(schoolId)
      return filterApps(
        applications,
        state.searchApplicationsFilter,
        state.statusFilter,
        state.scholasticYearFilter,
        state.startYearFilter,
        state.startDateFilter,
        state.applicationTypeFilter,
        state.intakeFilter,
        state.srnLinkFilter
      )
    },

    // list view filters
    statusFilter: (state) => state.statusFilter,
    scholasticYearFilter: (state) => state.scholasticYearFilter,
    startYearFilter: (state) => state.startYearFilter,
    startDateFilter: (state) => state.startDateFilter,
    applicationTypeFilter: (state) => state.applicationTypeFilter,
    intakeFilter: (state) => state.intakeFilter,
    srnLinkFilter: (state) => state.srnLinkFilter
  },
  mutations: {
    setApplications(state, { schoolId, applications }) {
      if (!state.localAreaApplications[schoolId]) {
        // init the object
        // eslint-disable-next-line import/no-named-as-default-member
        Vue.set(state.localAreaApplications, schoolId, [])
      }

      const allApplicationsMapped = applications.map((application) => {
        let item = {}

        let year = allScholasticYears.find(
          (year) => year.value === application.scholasticYear
        )
        item.scholasticYear = year ? year.text : ''

        item.scholasticYearCode = application.scholasticYear
        item.intendedStartDate = application.intendedStartDate

        item.name =
          application.student.surname + ', ' + application.student.firstName

        item.residencyStatus = application.residencyStatus ?? ''

        // To display Action required icon
        const { alertsFound, allDocumentViewed, applicationStatus } =
          application
        const allDocumentViewedStatus =
          allDocumentViewed === undefined ? true : allDocumentViewed
        const statusIsSent = applicationStatus === STATUS.SENT

        if (statusIsSent) {
          item.alertsFound = false
        } else if (!alertsFound && allDocumentViewedStatus) {
          item.alertsFound = false
        } else {
          item.alertsFound = true
        }

        item.requireAdditionalDocs = application.requireAdditionalDocs
        item.applicationStatus = application.applicationStatus
        item.dateReceived = application.dateReceived
        item.applicationID = application.applicationID
        item.studentDOB = application.student.dateOfBirth
        item.lastModifiedDate = application.lastModifiedDate

        item.isProcessed =
          EA_TAB_FILTERING['PROCESSED'].includes(applicationStatus)

        item.srn = application?.student?.srn
        item.ooaApplicationNo = application.ooaApplicationNo
        item.eoiID = application.eoiID

        // This should really be named enrolment type, see disambiguation on state.js
        item.applicationType = getEnrolmentType(application)
        item.enrolmentType = application.enrolmentType ?? ''
        item.isMEOOffer = application?.isMEOOffer || false

        if (
          [
            APPLICATION_OFFER_TYPE_NAME.SHS_OFFER,
            APPLICATION_OFFER_TYPE_NAME.MEO_SHS,
            APPLICATION_OFFER_TYPE_NAME.SCE_OFFER
          ].includes(item.applicationType)
        ) {
          item.inArea = '-'
        } else if (application.inArea === true) {
          item.inArea = 'In area'
        } else if (application.inArea === false) {
          item.inArea = 'Out-of-area'
        } else {
          item.inArea = '-'
        }

        return item
      })

      // use Vue.set to trigger updates on object modification
      // eslint-disable-next-line import/no-named-as-default-member
      Vue.set(state.localAreaApplications, schoolId, allApplicationsMapped)
    },
    setApplicationsFilter(state, value) {
      state.searchApplicationsFilter = value
    },
    setStatusFilter(state, value) {
      state.statusFilter = value
    },
    setScholasticYearFilter(state, value) {
      state.scholasticYearFilter = value
    },
    setStartYearFilter(state, value) {
      state.startYearFilter = value
    },
    setStartDateFilter(state, value) {
      state.startDateFilter = value
    },
    setApplicationTypeFilter(state, value) {
      state.applicationTypeFilter = value
    },
    setIntakeFilter(state, value) {
      state.intakeFilter = value
    },
    setSrnLinkFilter(state, value) {
      state.srnLinkFilter = value
    },
    setDefaultPage(state, value) {
      state.defaultPage = value
    }
  },
  actions: {
    getApplications(store) {
      if (!store.getters.selectedSchool) {
        return Promise.resolve()
      }

      const schoolCode = store.getters.selectedSchool.schoolCode

      return API.get(
        `${process.env.VUE_APP_API_PI}/v1/core/applications/submitted/${schoolCode}`,
        false,
        { schoolCode }
      ).then((response) => {
        const responseBody = response?.data?.body || {}
        const applications = responseBody?.studentApplications?.map((obj) => ({
          ...obj,
          ooa: false
        }))
        const data = {
          ...responseBody,
          studentApplications: applications
        }
        const allApplications = data.studentApplications

        return store.commit('setApplications', {
          schoolId: schoolCode,
          applications: allApplications
        })
      })
    },
    getLocalAreaApplication(store, [id]) {
      FIELD.setStore(store)

      return new Promise((done) => {
        store.commit('set', ['isEditing', false])
        store.commit('set', ['ernRecord', null])
        store.commit('set', ['application', null])
        // Scholastic years depend on the loaded application (includePreSchool),
        // so cached years from a previously viewed application must be cleared.
        if (store.state.referenceData) {
          store.commit('set', ['referenceData.scholasticYear', null])
        }
        store.dispatch('showSpinner')

        // Gets application record...
        const schoolCode = store.getters?.selectedSchool?.schoolCode
        const currentStream = store.getters.currentStream
        const isVirtualSelectiveSchool = store.getters?.isVirtualSelectiveSchool
        let localAreApplicationEndPoint = '/application/' + id
        let apiHeaders = {}
        if (isVirtualSelectiveSchool) {
          localAreApplicationEndPoint += '?source=shared'
        }
        if (currentStream === STREAM.ARCHIVE) {
          const applicationType = router.currentRoute.params.type
          localAreApplicationEndPoint = `${process.env.VUE_APP_API_PI}/v1/archived/application/${schoolCode}/${id}?applicationType=${applicationType}`
          apiHeaders.schoolCode = schoolCode
        }
        const isMeoOffer = id && id.includes('MEO')
        // Issued application
        if (isMeoOffer) {
          localAreApplicationEndPoint = `${process.env.VUE_APP_API_PI}/v1/meo/applications/submitted/${schoolCode}/${id}`
          apiHeaders.schoolCode = schoolCode
        }

        const isOesApplication = !isMeoOffer

        API.get(localAreApplicationEndPoint, true, apiHeaders).then(
          (response) => {
            let updatedResponse = response.data
            if (currentStream === STREAM.ARCHIVE || isMeoOffer) {
              updatedResponse = response.data.body
            }
            const oesApplication = updatedResponse?.oesApplication

            if (isMeoOffer && oesApplication) {
              oesApplication.enrolmentType ||= 'MAIN'
              oesApplication.isInArea ??= true
            }
            let allPromises = []

            if (isOesApplication) {
              oesApplication.isOesApplication = true
            }
            store.commit('set', ['application', oesApplication])
            // TODO no catch statements are attached to the following dispatch promises only one has been added to getLocalAreaSupportingDocs for FUS-1918. Further investigation is required to determine if the other dispatches require catch statements.
            const getReferenceData = store.dispatch('getReferenceData')
            allPromises.push(getReferenceData)
            const getScholasticYears = store.dispatch('getScholasticYears')
            allPromises.push(getScholasticYears)

            if (
              !_isEmpty(updatedResponse.oesApplication?.supportingDocuments)
            ) {
              //Get supporting documents for the application as the retrieve application API couldn't handle it
              let getSupportingDocuments = store.dispatch(
                'getLocalAreaSupportingDocs',
                id
              )
              allPromises.push(getSupportingDocuments)
            }
            const getGlobalRefData = store.dispatch('getGlobalRefData')
            allPromises.push(getGlobalRefData)
            // Gets all reference data...
            Promise.all(allPromises)
              .then((data) => {
                let referenceData = data[0]
                referenceData.scholasticYear = data[1]
                if (data[2]) {
                  updatedResponse.oesApplication.supportingDocuments =
                    mergeDocuments(
                      updatedResponse.oesApplication.supportingDocuments,
                      data[2]
                    )
                }
                store.commit('set', ['referenceData', referenceData])
              })
              .catch(() => {
                // TODO something if reference data or supporting docs isn't loaded
              })
              .finally(() => {
                store.dispatch('displayApplication', { ...updatedResponse })
                store.dispatch('hideSpinner')
                done()
              })
          }
        )
      })
    },
    getLocalAreaSupportingDocs({ getters }, id) {
      return new Promise((resolve, reject) => {
        // Gets local area application supporting documents
        let schoolCode = getters.application.schoolCode
        API.get(
          `${process.env.VUE_APP_API_PI}/v1/application/${schoolCode}/${id}/documentScanStatus`,
          true,
          { schoolCode }
        )
          .then((response) => {
            resolve(response.data.body.supportingDocuments)
          })
          .catch((error) => {
            reject(error)
          })
      })
    }
  }
}

export default applicationsModule
